import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';
import {
  AnalyticsService,
  CLIENT_EVENTS,
  GA4_EVENTS,
} from 'src/app/shared/analytics';
import { analyticsDistinctId, rawIdFromUserInfo } from 'src/app/shared/analytics/identity';
import { LegalDocKey, legalConsentPayload } from 'src/app/shared/legal/legal-documents';

type UiState = 'initial' | 'signup';
type OtpStep = 'identifier' | 'otp';

@Component({
  selector: 'app-customer-signup',
  templateUrl: './customer-signup.component.html',
  styleUrls: ['./customer-signup.component.scss']
})
export class CustomerSignupComponent implements OnInit, OnDestroy {
  uiState: UiState = 'initial';

  signupOtpStep: OtpStep = 'identifier';
  signupIdentifier = '';
  signupIdentifierError = '';
  signupOtpCode = '';
  signupOtpError = '';
  signupIsEmail = false;
  signupMaskedIdentifier = '';
  signupOtpTimer = 0;
  private signupOtpInterval: any;

  isLoading = false;

  // Terms of Use + Privacy Policy consent. Required before any signup method
  // (email/phone OTP or Google) can start.
  agreedToTerms = false;
  legalModal: LegalDocKey | null = null;
  private readonly consentRequiredMessage = 'Please agree to the Terms of Use and Privacy Policy to continue.';

  private destroy$ = new Subject<void>();
  private apiBaseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private analytics: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.checkGoogleCallback();
    if (this.route.snapshot.queryParamMap.get('start') === 'signup') {
      this.setUiState('signup');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearSignupOtpTimer();
  }

  setUiState(state: UiState): void {
    this.uiState = state;
    this.signupIdentifierError = '';
    this.signupOtpError = '';
    if (state === 'signup') {
      this.signupOtpStep = 'identifier';
      this.signupIdentifier = '';
      this.signupOtpCode = '';
    }
  }

  private readonly emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private readonly phoneRegex = /^[6-9]\d{9}$/;

  get isValidSignupIdentifier(): boolean {
    const val = this.signupIdentifier.trim();
    if (!val) return false;
    if (this.looksLikeEmail(val)) {
      return this.emailRegex.test(val);
    }
    return this.phoneRegex.test(val.replace(/\D/g, ''));
  }

  private looksLikeEmail(val: string): boolean {
    return val.includes('@') || /[a-zA-Z]/.test(val);
  }

  validateSignupIdentifier(): void {
    const val = this.signupIdentifier.trim();
    if (!val) {
      this.signupIdentifierError = '';
      return;
    }
    if (this.looksLikeEmail(val)) {
      this.signupIdentifierError = this.emailRegex.test(val)
        ? ''
        : 'Please enter a valid email address (e.g. name@company.com).';
      return;
    }
    const digits = val.replace(/\D/g, '');
    if (digits.length !== 10) {
      this.signupIdentifierError = 'Mobile number must be exactly 10 digits.';
      return;
    }
    if (!this.phoneRegex.test(digits)) {
      this.signupIdentifierError = 'Mobile number must start with 6, 7, 8 or 9.';
      return;
    }
    this.signupIdentifierError = '';
  }

  onSignupIdentifierChange(): void {
    if (this.signupIdentifierError) this.signupIdentifierError = '';
  }

  onConsentChange(): void {
    if (this.agreedToTerms && this.signupIdentifierError === this.consentRequiredMessage) {
      this.signupIdentifierError = '';
    }
  }

  agreeFromModal(): void {
    this.agreedToTerms = true;
    this.legalModal = null;
    this.onConsentChange();
  }

  sendSignupOtp(): void {
    const val = this.signupIdentifier.trim();
    if (!this.agreedToTerms) {
      this.signupIdentifierError = this.consentRequiredMessage;
      return;
    }
    if (!this.isValidSignupIdentifier) {
      this.signupIdentifierError = val.includes('@')
        ? 'Please enter a valid email address.'
        : 'Please enter a valid 10-digit mobile number.';
      return;
    }

    this.signupIdentifierError = '';
    this.isLoading = true;
    this.signupIsEmail = val.includes('@');

    const isEmail = val.includes('@');
    const url = isEmail
      ? `${this.apiBaseUrl}customer/email/register/send-otp`
      : `${this.apiBaseUrl}customer/phone/register/send-otp`;
    const payload = isEmail
      ? { email: val, team: 'salesonlinead' }
      : { phoneNumber: val.replace(/\D/g, ''), team: 'salesonlinead' };

    this.http.post<any>(url, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.success) {
            this.signupMaskedIdentifier = this.buildMaskedIdentifier(val, isEmail);
            this.signupOtpStep = 'otp';
            this.startSignupOtpTimer();
            this.showSuccess(isEmail ? 'OTP sent to your email!' : 'OTP sent successfully!');
          } else {
            this.signupIdentifierError = response?.message || 'Failed to send OTP';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.signupIdentifierError = this.extractErrorMessage(error, 'Failed to send OTP');

          if (error.status === 409 && error.error?.code === 'ACCOUNT_EXISTS') {
            this.signupIdentifierError = 'Account already exists. Please login instead.';
            setTimeout(() => {
              this.router.navigate(['/customers/login']);
            }, 3000);
          }
        }
      });
  }

  verifySignupOtp(): void {
    // The checkbox stays visible on the OTP step, so it can be unticked there.
    if (!this.agreedToTerms) {
      this.signupOtpError = this.consentRequiredMessage;
      return;
    }
    if (!this.signupOtpCode || this.signupOtpCode.length !== 6) {
      this.signupOtpError = 'Please enter the 6-digit OTP.';
      return;
    }

    this.signupOtpError = '';
    this.isLoading = true;

    const val = this.signupIdentifier.trim();
    const isEmail = val.includes('@');
    const url = isEmail
      ? `${this.apiBaseUrl}customer/email/register/verify-otp`
      : `${this.apiBaseUrl}customer/phone/register/verify-otp`;
    // Consent travels with the request that creates the account, so the
    // backend can store it against the new customer id in the same call.
    const consent = legalConsentPayload();
    const payload = isEmail
      ? { email: val, otp: this.signupOtpCode, ...consent }
      : { phoneNumber: val.replace(/\D/g, ''), otp: this.signupOtpCode, ...consent };

    this.http.post<any>(url, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.success) {
            this.handleSignupSuccess(response, isEmail);
          } else {
            this.signupOtpError = response?.message || 'Invalid OTP';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.signupOtpError = this.extractErrorMessage(error, 'Invalid OTP. Please try again.');
        }
      });
  }

  resendSignupOtp(): void {
    if (this.signupOtpTimer > 0 || this.isLoading) return;
    this.signupOtpCode = '';
    this.signupOtpError = '';
    this.sendSignupOtp();
  }

  goBackToSignupIdentifier(): void {
    this.signupOtpStep = 'identifier';
    this.signupOtpCode = '';
    this.signupOtpError = '';
    this.clearSignupOtpTimer();
  }

  formatSignupOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '').slice(0, 6);
    this.signupOtpCode = input.value;
  }

  private buildMaskedIdentifier(val: string, isEmail: boolean): string {
    if (isEmail) {
      const [name, domain] = val.split('@');
      if (!name || !domain) return val;
      const visible = name.slice(0, Math.min(2, name.length));
      return `${visible}${'*'.repeat(Math.max(name.length - 2, 2))}@${domain}`;
    }
    const digits = val.replace(/\D/g, '');
    if (digits.length < 4) return digits;
    return `******${digits.slice(-4)}`;
  }

  private startSignupOtpTimer(): void {
    this.clearSignupOtpTimer();
    this.signupOtpTimer = 60;
    this.signupOtpInterval = setInterval(() => {
      this.signupOtpTimer--;
      if (this.signupOtpTimer <= 0) this.clearSignupOtpTimer();
    }, 1000);
  }

  private clearSignupOtpTimer(): void {
    if (this.signupOtpInterval) {
      clearInterval(this.signupOtpInterval);
      this.signupOtpInterval = null;
    }
    this.signupOtpTimer = 0;
  }

  private handleSignupSuccess(response: any, isEmail: boolean): void {
    const token = response.access_token;
    const user = response.user || {};

    if (!token) {
      this.signupOtpError = 'Authentication token missing';
      return;
    }

    localStorage.setItem('accessToken', token);
    localStorage.setItem('tokenType', response.token_type || 'Bearer');
    localStorage.setItem('tokenExpiry', (Date.now() + (response.expires_in || 86400) * 1000).toString());
    localStorage.setItem('authType', isEmail ? 'email_otp' : 'phone_otp');

    const userInfo = {
      id: user.external_id,
      email: user.email,
      name: user.name,
      'custom:team': user.team,
      'custom:visibility': user.user_type || 'customer',
      company: user.company,
      phone_number: user.phone_number || (isEmail ? '' : this.signupIdentifier.replace(/\D/g, '')),
      phone_number_verified: !isEmail,
      auth_type: isEmail ? 'email_otp' : 'phone_otp',
      profile_complete: user.profile_complete || false
    };

    localStorage.setItem('userInfo', JSON.stringify(userInfo));

    // Email registrations: always send the "registration complete" email,
    // fired before any redirect and with no conditions.
    if (isEmail) {
      this.sendRegistrationCompleteEmail(userInfo.email, userInfo.name);
    }

    // Analytics: identify + fire the signup conversion events. Runs from
    // both the email-OTP and phone-OTP branches (Google callback fires the
    // same events from processGoogleCallback below).
    try {
      if (userInfo.id) {
        const distinctId = analyticsDistinctId('customer', rawIdFromUserInfo('customer', userInfo));
        this.analytics.identify({
          distinctId: distinctId ?? '',
          role: 'customer',
          properties: {
            email: userInfo.email,
            name: userInfo.name,
            phone: userInfo.phone_number,
            company_name: userInfo.company,
          },
        });
        const method = isEmail ? 'email_otp' : 'phone_otp';
        this.analytics.track(GA4_EVENTS.SIGN_UP, { method, role: 'customer' });
        this.analytics.track(CLIENT_EVENTS.CLIENT_REGISTERED, {
          method,
          profile_complete: !!userInfo.profile_complete,
        });
      }
    } catch { /* never break signup */ }

    if (!userInfo.profile_complete) {
      this.router.navigate(['/customers/profile'], {
        queryParams: { from: 'signup' }
      });
    } else {
      this.showSuccess('Account created successfully!');
      this.router.navigate(['workspace/customer/dashboard']);
    }
  }

  signInWithGoogle(): void {
    if (!this.agreedToTerms) {
      this.signupIdentifierError = this.consentRequiredMessage;
      return;
    }
    this.isLoading = true;
    // The backend holds these in the session across the Google round-trip and
    // stores them only if the callback turns out to create a new account.
    const consent = legalConsentPayload();
    const query = new URLSearchParams({
      consent: '1',
      terms_version: consent.terms_version,
      privacy_version: consent.privacy_version,
    });
    window.location.href = `${this.apiBaseUrl}auth/google?${query.toString()}`;
  }

  private checkGoogleCallback(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['access_token']) {
        this.processGoogleCallback(params);
      }
    });
  }

  private processGoogleCallback(params: any): void {
    const token = params['access_token'];
    if (token) {
      localStorage.setItem('accessToken', token);
      localStorage.setItem('tokenType', params['token_type'] || 'bearer');
      localStorage.setItem('tokenExpiry', (Date.now() + (parseInt(params['expires_in']) || 86400) * 1000).toString());
      localStorage.setItem('authType', 'google');

      if (params['user']) {
        try {
          const user = JSON.parse(atob(params['user']));
          localStorage.setItem('userInfo', JSON.stringify(user));

          // Analytics: identify + signup events for the Google-OAuth path.
          if (user && (user.id || user.external_id)) {
            const distinctId = analyticsDistinctId('customer', rawIdFromUserInfo('customer', user));
            this.analytics.identify({
              distinctId: distinctId ?? '',
              role: 'customer',
              properties: {
                email: user.email,
                name: user.name,
                phone: user.phone_number,
                company_name: user.company,
              },
            });
            this.analytics.track(GA4_EVENTS.SIGN_UP, { method: 'google', role: 'customer' });
            this.analytics.track(CLIENT_EVENTS.CLIENT_REGISTERED, {
              method: 'google',
              profile_complete: !!user.profile_complete,
            });
          }
        } catch (e) {
          console.error('Failed to parse user data', e);
        }
      }

      this.showSuccess('Google sign-up successful!');
      this.router.navigate(['/workspace/projects']);
    }
    this.isLoading = false;
    this.clearUrlParameters();
  }

  private clearUrlParameters(): void {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  // Fire-and-forget "registration complete — complete your profile" email.
  // Failures are logged only; they must not block the signup flow.
  // NOTE: deliberately NOT piped through takeUntil(destroy$). This is called
  // right before router.navigate(), which destroys the component and would
  // otherwise cancel the in-flight request before it reaches the backend.
  private sendRegistrationCompleteEmail(email: string, name: string): void {
    if (!email) return;
    this.http.post(`${this.apiBaseUrl}send-client-registration-complete`, {
      mailto: email,
      client_name: name || 'there'
    })
      .subscribe({
        next: () => {},
        error: (err) => console.error('Failed to send registration-complete email:', err)
      });
  }

  private extractErrorMessage(error: any, fallback: string): string {
    return error?.error?.message || error?.error?.detail || fallback;
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }
}
